import React, { FunctionComponent, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Label, Switch, Colors } from '@inplayer-org/inplayer-ui';
import { css, FlattenSimpleInterpolation } from 'styled-components';
import { MdInfoOutline } from 'react-icons/md';
import keys from 'lodash/keys';
import mapValues from 'lodash/mapValues';

// utils
import { isDevelopmentEnv } from 'utils/helpers';

// hooks
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';
import useBoolean from 'hooks/useBoolean';

// rules
import Rules from 'restrictions/roles/constants/rules';

// actions
import { openModal } from 'ducks/merchantDucks/modal/actions';
import { deleteResource } from 'ducks/merchantDucks/items/actions';

// selectors
import { getItemChatRoom, getItemDetails, getItemLiveLikeResources } from 'ducks/merchantDucks/items/selectors';

// types
import { ModalNames } from 'ducks/merchantDucks/modal/types';

// styled components
import { ContentContainer, CenterContent, Description } from 'components/SharedStyledElements';
import { TooltipStyled } from 'pages/MerchantPages/Assets/Vouchers/shared/sharedComponents';

// components
import CommonIcon from 'components/CommonIcon';
import WidgetsContainer from 'pages/MerchantPages/Assets/components/LiveLike/WidgetsContainer';
import DeleteWidgetSettingsModal from 'pages/MerchantPages/Assets/components/LiveLike/modals/DeleteWidgetSettingsModal';
import AttentionMessage from 'components/AttentionMessage';

interface Props {
  closePanel: () => void;
}

const { Container, Cell } = Grid;

const settingsIconStyles = (isChatEnabled: boolean) => {
  let style = css`
    margin-left: 1rem;
  `;

  if (isChatEnabled) {
    style = css`
      ${style}
      > svg {
        color: ${Colors.fontDarkGray};
      }
    `;
  }

  return style;
};

const LiveLikeContainer: FunctionComponent<Props> = ({ closePanel }) => {
  const dispatch = useDispatch();
  const { hasRule } = useRestrictionRules();
  const isReadOnly = hasRule(Rules.READONLY_ASSETS_AND_PACKAGES);

  // Selectors
  const itemLiveLikeResources = useSelector(getItemLiveLikeResources);
  const { id: itemId } = useSelector(getItemDetails);
  const itemChatRoomId = useSelector(getItemChatRoom)?.id || null;

  const [enabledWidgets, setEnabledWidgets] = useState<{ [key: string]: boolean }>({});
  const [widget, setWidget] = useState('');
  const [deletedWidget, setDeletedWidget] = useState('');
  const backToWidgets = useBoolean(false);

  useEffect(() => {
    setEnabledWidgets(mapValues(itemLiveLikeResources, (value) => value.length !== 0));
  }, [itemLiveLikeResources, backToWidgets]);

  const handleOpenConfirmModal = () => {
    dispatch(openModal(ModalNames.WidgetRemove));
  };

  const handleToggleWidget = (widgetName: string) => {
    setEnabledWidgets((prevState) => {
      // delete widget setting when switch is turned off
      if (prevState[widgetName]) {
        setDeletedWidget(widgetName);
        handleOpenConfirmModal();
      }
      // set enabled widget to state in order to
      // show widget details screen
      if (!prevState[widgetName]) {
        setWidget(widgetName);
        // set backToWidget to false to call useEffect
        // and turn off the switch if item widget settings
        // are not set
        backToWidgets.setFalse();
        return { ...prevState, [widgetName]: !prevState[widgetName] };
      }

      return prevState;
    });
  };

  const livelikeWidgets = {
    chatrooms: {
      id: 'chatrooms',
      title: 'Chat',
      tooltipContent: 'The chat will be displayed before and after asset purchase',
      isEnabled: enabledWidgets.chatrooms,
      toggleWidget: () => handleToggleWidget('chatrooms'),
      handleSettingsClick: () => setWidget('chatrooms'),
    },
  };

  const handleRemoveWidgetSettings = async () => {
    switch (deletedWidget) {
      case 'chatrooms':
        await dispatch(deleteResource(itemChatRoomId!, itemId));
        break;
      default:
        break;
    }

    setEnabledWidgets((prevState) => ({ ...prevState, [deletedWidget]: false }));
  };

  const handleBackToWidgets = (resetForm?: () => void) => {
    backToWidgets.setTrue();
    setWidget('');

    if (resetForm) {
      resetForm();
    }
  };

  const itemLiveLikeWidgets = keys(itemLiveLikeResources);

  return backToWidgets && widget === '' ? (
    <ContentContainer>
      <CenterContent>
        {isDevelopmentEnv && <AttentionMessage />}
        <Container rowGap="1.75rem" columns={12}>
          <Cell width={12}>
            <Description variant="p">
              To use the widgets, make sure you have completed the LiveLike integration in the third-party integrations
              section.
            </Description>
          </Cell>
          {itemLiveLikeWidgets.map((itemWidget: any) => {
            let commonIconProps: {
              name: string;
              hoverStatus: string;
              isIconDisabled: boolean;
              styles: FlattenSimpleInterpolation;
              onClick?: () => void;
            } = {
              name: 'settings',
              hoverStatus: 'settings',
              isIconDisabled: !livelikeWidgets[itemWidget].isEnabled,
              styles: settingsIconStyles(livelikeWidgets[itemWidget].isEnabled),
            };

            if (livelikeWidgets[itemWidget].isEnabled) {
              commonIconProps = {
                ...commonIconProps,
                onClick: livelikeWidgets[itemWidget].handleSettingsClick,
              };
            }

            return (
              <Cell width={12} key={livelikeWidgets[itemWidget].id}>
                <Container columns={12}>
                  <Cell width={2}>
                    <Label>{livelikeWidgets[itemWidget].title}</Label>
                    <TooltipStyled
                      tag="tooltip_chat"
                      placement="right"
                      content={livelikeWidgets[itemWidget].tooltipContent}
                    >
                      <MdInfoOutline />
                    </TooltipStyled>
                  </Cell>
                  <Cell width={2}>
                    <Switch
                      tag="toggle_chat"
                      id={livelikeWidgets[itemWidget].id}
                      label=""
                      checked={livelikeWidgets[itemWidget].isEnabled}
                      onChange={livelikeWidgets[itemWidget].toggleWidget}
                      disabled={isReadOnly}
                    />
                  </Cell>
                  <Cell width={2}>
                    <CommonIcon tag="icon_settings_chat" {...commonIconProps} />
                  </Cell>
                </Container>
              </Cell>
            );
          })}
          <DeleteWidgetSettingsModal handleRemoveWidgetSettings={handleRemoveWidgetSettings} />
        </Container>
      </CenterContent>
    </ContentContainer>
  ) : (
    <WidgetsContainer widget={widget} closePanel={closePanel} handleBackToWidgets={handleBackToWidgets} />
  );
};

export default LiveLikeContainer;
